/* global Office */
/**
 * useLicense Hook
 * Manages license data and API calls.
 *
 * Flow:
 * 1. On mount: call acquireOid() from identityService to get the Azure AD OID
 * 2. OID is stored in React state ONLY — never written to localStorage or sessionStorage
 * 3. Call getLicenseStatus(oid) → backend auto-creates user with FREE license if needed
 * 4. Extract and return settings data separately (managed by useSettings hook)
 *
 * OID acquisition rules:
 * - Source: OfficeRuntime.auth.getAccessToken() + JWT oid claim decode
 * - On exception: retry once after 500ms (handled inside identityService)
 * - On OID missing in token: error shown immediately, no retry
 * - No email/displayName fallback — OID is the sole identity
 * - OID change detection: if OID changes mid-session, show account-change error
 */

import { useEffect, useState, useCallback, useRef } from "react";
import {
  getLicenseStatus,
  activateLicense as activateLicenseApi,
} from "../api/licenseApi";
import { acquireOid } from "../services/identityService";
import { translate } from "../i18n";

const STORAGE_KEY = "avamboo_license";

export default function useLicense() {
  const [license, setLicense] = useState(null);
  const [settings, setSettings] = useState(null);
  const [oid, setOid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [isInitialLoadSuccess, setIsInitialLoadSuccess] = useState(false);
  const [isOfficeReady, setIsOfficeReady] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  // Prevent multiple initializations across StrictMode double-invocations
  const hasInitializedRef = useRef(false);
  // Cache the OID in a ref for change detection across re-authentications
  const cachedOidRef = useRef(null);

  /**
   * Load license status from backend.
   * Backend auto-creates user and assigns FREE license if user doesn't exist.
   */
  const loadLicenseStatus = useCallback(async (currentOid) => {
    if (!currentOid) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      const result = await getLicenseStatus(currentOid);

      if (result.success && result.data) {
        const licenseData = {
          benutzerId: result.data.benutzerId,
          lizenzId: result.data.lizenzId,
          lizenzKey: result.data.lizenzKey,
          istAktiv: result.data.istAktiv,
          lizenzTyp: result.data.lizenzTyp,
          maxEmailsProTag: result.data.maxEmailsProTag,
          maxEmailsProMonat: result.data.maxEmailsProMonat,
        };

        const settingsData = result.data.benutzerSettings;

        setLicense(licenseData);
        setSettings(settingsData);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(licenseData));
        // Cache settings for commands.js (OnMessageSend runs in an isolated context
        // and cannot make async API calls — it reads this cache synchronously).
        if (settingsData) {
          localStorage.setItem("avamboo_settings_cache", JSON.stringify(settingsData));
        }
        setIsInitialLoadSuccess(true);

        return { licenseData, settingsData };
      } else {
        setErrorMsg(result.message);
        setIsInitialLoadSuccess(false);
        return null;
      }
    } catch (error) {
      console.error("Error loading license status:", error);
      setErrorMsg(translate("Fehler beim Laden der Lizenzdaten"));
      setIsInitialLoadSuccess(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initialize: acquire OID via OfficeRuntime.auth, then load license.
   * Runs once on mount.
   */
  const initialize = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);

    const { oid: acquiredOid, error: oidError } = await acquireOid();
    setIsOfficeReady(true);

    if (!acquiredOid) {
      setErrorMsg(oidError || translate("Authentifizierung fehlgeschlagen."));
      setLoading(false);
      return;
    }

    // OID change detection: if a different OID is returned in the same session, stop.
    if (cachedOidRef.current && cachedOidRef.current !== acquiredOid) {
      setErrorMsg(
        translate("Das Konto hat sich geändert. Bitte schließen Sie das Add-in und öffnen Sie es erneut.")
      );
      setLoading(false);
      return;
    }
    cachedOidRef.current = acquiredOid;

    setOid(acquiredOid);

    // Read email from Office.js (sync, no cost) — used for prefill in Password Manager
    try {
      const email = Office?.context?.mailbox?.userProfile?.emailAddress;
      if (email) setUserEmail(email);
    } catch {
      // Office.js not available (dev mode) — ignore
    }

    await loadLicenseStatus(acquiredOid);
  }, [loadLicenseStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps — run once on mount only

  /**
   * Auto-clear success message after 8 seconds.
   */
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  /**
   * Activate a new license key.
   */
  const activateLicense = async (licenseKey) => {
    if (!oid) {
      setErrorMsg(translate("OID nicht verfügbar"));
      return false;
    }

    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const result = await activateLicenseApi(licenseKey, oid);

      if (result.success) {
        setSuccessMsg(result.message);
        await loadLicenseStatus(oid);
        return true;
      } else {
        setErrorMsg(result.message);
        return false;
      }
    } catch (error) {
      console.error("Error activating license:", error);
      setErrorMsg(translate("Serverfehler bei der Lizenzaktivierung"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear license (for "re-activate" flow).
   * Does not delete from backend — only clears local state.
   */
  const clearLicense = () => {
    setLicense(null);
    setSettings(null);
    localStorage.removeItem(STORAGE_KEY);
    setSuccessMsg(null);
    setErrorMsg(null);
  };

  /**
   * Reload license status (useful after settings update).
   */
  const reloadLicenseStatus = async () => {
    if (oid) {
      return await loadLicenseStatus(oid);
    }
  };

  /**
   * Clear error message.
   */
  const clearError = () => {
    setErrorMsg(null);
  };

  return {
    license,       // License data only (no settings)
    settings,      // Settings data extracted from license response
    oid,           // User's Azure AD OID (memory-only, never persisted)
    userEmail,     // User's email from Office.js (for prefill, null in dev mode)
    loading,       // Loading state
    errorMsg,      // Error message
    successMsg,    // Success message
    isInitialLoadSuccess, // True if backend responded successfully on first load
    isOfficeReady, // True once OfficeRuntime.auth has been called

    activateLicense,     // Activate new license
    clearLicense,        // Clear local license state
    reloadLicenseStatus, // Reload from backend
    clearError,          // Clear error message
    retryAuth: initialize, // Retry OID acquisition (e.g. after popup was unblocked)
  };
}
